import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import Global from '../../store/global-selectors';
import { useModal } from '../../hooks/use-modal';
import { sellerProvidersUpdated } from '../../store/seller/seller.actions';
import { useWalletSetting } from '../../wallet/contexts/wallet-data-context-provider';
import useWalletAddFunds from '../../pages/settings/payment-and-subscription/hooks/use-wallet-add-funds';
import { walletSettingsEnum } from '../../enums/wallet-settings';
import useWalletSaveAutoFunding from '../../pages/settings/providers/add-funds-modal/hooks/use-wallet-save-auto-funding';
import useSaveWalletDefaultFundingAmount from '../../pages/settings/payment-and-subscription/hooks/use-save-wallet-default-funding-amount';
import { AddFundsModal } from '../../pages/settings/payment-and-subscription/components/add-funds-modal/add-funds-modal';
import { AutoFundingModal } from '../../pages/settings/payment-and-subscription/components/auto-fund-modal/auto-funding-modal';
import { useWallet } from '../../wallet/hooks/use-wallet';
import { EventSourceStrings, Workflows } from '../../tracking/tracking-constants';

import messages from './messages';
import styles from './style.module.less';
import { WalletRegistrationOrUpdateButton } from '../../wallet/components/wallet-registration-or-update-button';
import { useTrackWorkflow, WorkflowSteps } from '../../tracking/use-workflow-tracking';

export const AddFundsLink = ({ className = '', walletId, isManageFunds = false, trackingId }) => {
  const { close: closeAddFundsModal, isOpen: addFundsModalIsOpen, open: openAddFundsModal } = useModal();
  const { close: closeAutoFundConfigModal, isOpen: autoFundConfigModalIsOpen, open: openAutoFundConfigModal } = useModal();

  const { autoFundConfig, balance, defaultPurchaseConfig, isSetupComplete = false } = useWallet(walletId);
  const sellerDefaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const sellerCurrencyCode = balance?.code || sellerDefaultCurrencyCode;
  const { canUseSetting: allowDefaultPurchase } = useWalletSetting(walletId, walletSettingsEnum.defaultPurchaseAmount);
  const preventDefault = (e) => e?.preventDefault();
  const trackAddFunds = useTrackWorkflow(Workflows.ADD_FUNDS_FLOW);
  const onSaveWalletDefaultFundingAmount = useSaveWalletDefaultFundingAmount(walletId);
  const onAddFunds = useWalletAddFunds(walletId);
  const onSaveAutoFundConfig = useWalletSaveAutoFunding(walletId);

  const dispatch = useDispatch();
  const onSuccess = useCallback(() => {
    dispatch(sellerProvidersUpdated());
    closeAddFundsModal();
  }, [closeAddFundsModal, dispatch]);

  const onAddFundsClick = useCallback(
    (e) => {
      e.stopPropagation();
      trackAddFunds(WorkflowSteps.START, { source: trackingId });
      return openAddFundsModal();
    },
    [openAddFundsModal, trackAddFunds, trackingId],
  );

  const onAutoFundClick = useCallback(
    (e) => {
      e.stopPropagation();
      openAutoFundConfigModal();
    },
    [openAutoFundConfigModal],
  );

  const AddFundsButton = () => (
    <Button className={className} onClick={onAddFundsClick} theme='link'>
      <FormattedMessage {...messages.addFundsButton} />
    </Button>
  );
  const DropdownButton = () => (
    <DropdownMenu menuPosition='bottom-right'>
      <DropdownMenuToggler
        className={styles['menu-toggle']}
        hideChevron
        onClick={preventDefault}
        openClassName={styles['open-menu-toggle']}
        theme='link'
      >
        <FormattedMessage {...messages.manageFundsButton} />
      </DropdownMenuToggler>
      <DropdownMenuItem onClick={onAddFundsClick}>
        <FormattedMessage {...messages.addFundsButton} />
      </DropdownMenuItem>
      <DropdownMenuItem onClick={onAutoFundClick}>
        <FormattedMessage {...messages.manageAutoFunding} />
      </DropdownMenuItem>
    </DropdownMenu>
  );

  const actionButton = isManageFunds ? <DropdownButton /> : <AddFundsButton />;

  return (
    <>
      <WalletRegistrationOrUpdateButton trackingId={EventSourceStrings.WALLET_BALANCE_INFO} walletId={walletId}>
        {({ openModal: openWalletSetupModal }) =>
          isSetupComplete ? (
            actionButton
          ) : (
            <Tooltip position='bottom-right' shouldHideTooltip={isSetupComplete}>
              <Button className={className} onClick={openWalletSetupModal} theme='link'>
                <FormattedMessage {...messages.addFundsButton} />
              </Button>
              <TooltipContent contentClassName={styles['wallet-tooltip']}>
                <FormattedMessage {...messages.registerWallet} />
              </TooltipContent>
            </Tooltip>
          )
        }
      </WalletRegistrationOrUpdateButton>
      {addFundsModalIsOpen && (
        <AddFundsModal
          allowDefaultPurchase={allowDefaultPurchase}
          balance={balance}
          defaultPurchaseConfig={defaultPurchaseConfig}
          onAddFunds={onAddFunds}
          onClose={closeAddFundsModal}
          onSaveDefaultFundingAmount={onSaveWalletDefaultFundingAmount}
          onSuccess={onSuccess}
          sellerCurrencyCode={sellerCurrencyCode}
          trackingId={trackingId}
        />
      )}
      {autoFundConfigModalIsOpen && (
        <AutoFundingModal
          autoFundConfig={autoFundConfig}
          onClose={closeAutoFundConfigModal}
          onSaveAutoFundConfig={onSaveAutoFundConfig}
          onSuccess={onSuccess}
          sellerCurrencyCode={sellerCurrencyCode}
          walletId={walletId}
        />
      )}
    </>
  );
};

AddFundsLink.propTypes = {
  className: PropTypes.string,
  walletId: PropTypes.string,
  isManageFunds: PropTypes.bool,
  trackingId: PropTypes.string.isRequired,
};
